import _ from "lodash";

import { AxiosError } from "axios";

import { toast } from "@/design-system/components/Toast/Toast";

import { TransformDefinitionSet } from "./transformationDefinition";
import { makeRequest } from "../util";
import { DestinationKey } from "../destinations/types";
import {
  GetTransformationSetsResponse,
  TransformationIntention,
  TransformationPageFilter,
  TransformationPageSort,
} from "./types";

export const loadTransforms = (
  setAllTransformationSets: (
    value:
      | ((prevState: TransformDefinitionSet[]) => TransformDefinitionSet[])
      | TransformDefinitionSet[],
  ) => void,
  setLoaded?: (value: ((prevState: boolean) => boolean) | boolean) => void,
  /** Replaces the default error toast. */
  onError?: (error: AxiosError) => void,
): void => {
  makeRequest<any>("get_transformation_sets", {})
    .then((response: { data: GetTransformationSetsResponse }) => {
      setAllTransformationSets(
        _.map(response.data, (setData) => new TransformDefinitionSet(setData)),
      );
      setLoaded && setLoaded(true);
    })
    .catch((error: AxiosError) => {
      if (onError) {
        onError(error);
      } else if (error.response?.status !== 403) {
        toast.error("Something went wrong. Unable to load page.");
        console.error(error.message);
      }
    });
};

export type NewTransformationSet = {
  intention: TransformationIntention;
  destinationName: DestinationKey | null;
  eventDefId: string | null;
  eventType: "track" | "identify";
};

/** Creates an empty transformation set and returns its ID. */
export async function createTransformationSet(
  set: NewTransformationSet,
): Promise<string> {
  const response = await makeRequest<{
    transformationSetId?: string;
    errorMessage?: string;
  }>("save_new_transformation_group", { ...set, transformations: [] });
  if (response.data.errorMessage || !response.data.transformationSetId) {
    throw new Error(
      response.data.errorMessage ?? "No transformation set was created",
    );
  }
  return response.data.transformationSetId;
}

export async function deleteTransformationSet(id: string): Promise<void> {
  await makeRequest("delete_transformation_set", { id });
}

export const organizeTransformations = (
  allTransformations: TransformDefinitionSet[],
  options: {
    filter?: TransformationPageFilter | null;
    sort?: TransformationPageSort | null;
    includeAll?: boolean;
  },
): TransformDefinitionSet[] => {
  const { filter, sort = false } = options;
  let result = [...allTransformations];

  if (filter?.type === "search" && filter.value) {
    const term = filter.value.toLowerCase();

    result = _.filter(
      result,
      (t) =>
        t.destinationName?.toLowerCase().includes(term) ||
        t.eventDefName?.toLowerCase().includes(term) ||
        t.transformations.some(
          (transformation) =>
            String(transformation.operation?.targetValue)
              .toLowerCase()
              .includes(term) ||
            String(transformation.operation?.operandValue)
              .toLowerCase()
              .includes(term),
        ),
    );
  }

  if (sort) {
    result = _.orderBy(
      result,
      [sort.column, "createdAt"],
      [sort.direction, sort.direction],
    );
  }

  return result;
};
